// ---------------------------------------------------------------------------
// P1b — gravador de voo da navegação (só diagnóstico, só com "?dbg=1"). NÃO altera comportamento.
//
// T1  Registro de escritas: com a flag, os métodos e as vistas (alvo/posição) da navegação ancorada são embrulhados
//     NA INSTÂNCIA (instrumentNav): cada escrita chama o original com os mesmos argumentos e só anota fonte, antes,
//     depois e Δ (índice ancorado = a vela; distância do fim). A fonte vem de quem escreve (navSource antes da
//     escrita, navSource(0) depois) ou do próprio método (vela ao vivo, re-âncora).
// T2  Dedo × posição: o arrasto informa, por movimento, o Δ pedido pelo dedo (velas, antes da trava), o Δ aplicado
//     (depois) e a trava que atuou (borda mais antiga carregada, borda mais nova carregada, presente, nenhuma).
// T3  Contra-arrasto (com o dedo na tela): (a) a vista anda no sentido OPOSTO ao do dedo; (b) "segura": anda menos
//     de 30% do pedido por 3 quadros seguidos; (c) a posição muda por escrita de outra fonte.
// T4  Gravador de voo: os últimos 120 quadros congelados a cada "Marcar" e a cada contra-arrasto (até 20).
// Tudo em buffers circulares pré-alocados; zero alocação por quadro (os relatórios alocam, fora do caminho quente).
// ---------------------------------------------------------------------------

import { DBG_ENABLED } from './dbgFlag';
import { jank, MODE_NAMES } from './jankRecorder';

const f = (x: number, d = 1) => (isFinite(x) ? x.toFixed(d) : '—');

/** Fontes de escrita (a ordem é a dos relatórios). */
export const SRC = {
  OUTRA: 0, ARRASTO: 1, LACO: 2, AUTOPLAY: 3, CONGELAR: 4, VIVO: 5, CARGA_ANTIGA: 6, CARGA_NOVA: 7, COSTURA: 8,
  TRAVA: 9, TROCA: 10, IR_TEMPO: 11, X_ROLETA: 12, REANCORA: 13, JANELA2D: 14,
} as const;
export const SRC_NAMES = ['outra', 'arrasto', 'laço (posição segue o alvo)', 'autoplay', 'congelar', 'vela ao vivo', 'carga mais antiga',
  'carga mais nova', 'costura', 'trava da borda', 'troca de série', 'irParaTempo', 'X/roleta', 're-âncora', 'janela 2D'];
const NSRC = SRC_NAMES.length;
/** Trava do arrasto. */
export const LIM = { NENHUMA: 0, ANTIGA: 1, NOVA: 2, PRESENTE: 3 } as const;
export const LIM_NAMES = ['nenhuma', 'borda mais antiga carregada', 'borda mais nova carregada', 'presente'];
const LIM_SHORT = ['-', 'antiga', 'nova', 'presente'];
const WHICH = ['alvo', 'posição'];

const W_CAP = 600, F_CAP = 240, SNAP_FRAMES = 120, SNAP_CAP = 24, SNAP_EVENTS = 20, EV_CAP = 200;
const HOLD_RATIO = 0.3, HOLD_FRAMES = 3, OPP_MIN = 3, LOOKBACK = 30;
export const EV_KIND = ['sentido oposto', 'segura', 'outra fonte'];
const BAND_NAMES = ['< 10 velas', '< 1.000 velas', '≥ 1.000 velas'];

export interface NavLike {
  targetIndex: number; positionIndex: number; targetDist: number; posDist: number;
  target: { current: number }; position: { current: number };
  liveAppended(k: number): void; dataShifted(n: number): void; setTargetRel(d: number, g: number): void; setPosRel(d: number, g: number): void; reanchor(): void;
}

export class NavRecorder {
  /** Fonte da escrita em curso (quem escreve marca antes e volta a 0 depois). */
  src = 0;
  /** A janela de velas contém o presente? (o App informa; decide se a trava do 0 é "presente" ou "borda mais nova") */
  atPresent = true;
  private nav: NavLike | null = null;

  // ---------------------------------------------------------------- T1: escritas (anel de 600)
  wN = 0; private wHead = 0;
  wT = new Float64Array(W_CAP); wFrame = new Uint32Array(W_CAP); wSrc = new Uint8Array(W_CAP); wWhich = new Uint8Array(W_CAP);
  wBefore = new Float64Array(W_CAP); wAfter = new Float64Array(W_CAP); wDistBefore = new Float64Array(W_CAP); wDistAfter = new Float64Array(W_CAP);
  readonly srcCount = new Float64Array(NSRC);
  /** escritas deste quadro: máscara de fontes; e se a POSIÇÃO mudou por fonte que não é arrasto/laço */
  private frameSrcMask = 0; private framePosOther = 0; private framePosOtherSrc = 0;
  note(src: number, which: number, before: number, after: number, distBefore: number, distAfter: number): void {
    if (before === after && distBefore === distAfter) return;
    const i = this.wHead; this.wHead = (i + 1) % W_CAP; this.wN++;
    // horário do último quadro fechado + número do quadro (ler o relógio aqui alocaria um número por escrita)
    this.wT[i] = this.prevT; this.wFrame[i] = this.fN; this.wSrc[i] = src; this.wWhich[i] = which;
    this.wBefore[i] = before; this.wAfter[i] = after; this.wDistBefore[i] = distBefore; this.wDistAfter[i] = distAfter;
    this.srcCount[src]++;
    this.frameSrcMask |= 1 << src;
    if (which === 1 && before !== after && src !== SRC.ARRASTO && src !== SRC.LACO) { this.framePosOther += after - before; this.framePosOtherSrc |= 1 << src; }
  }

  /**
   * Embrulha os escritores de uma navegação ancorada NA INSTÂNCIA. Cada embrulho chama o original com os mesmos
   * argumentos e devolve o mesmo resultado; só lê antes e depois e anota.
   */
  instrument(nav: NavLike): void {
    if (this.nav) return;
    this.nav = nav;
    const R = this, n = nav as any;
    const wrapM = (name: string, own: number) => {
      const orig = n[name];
      n[name] = function (a: any, b: any) {                  // todos os escritores têm até 2 argumentos (sem array por chamada)
        const t0 = nav.targetIndex, p0 = nav.positionIndex, td0 = nav.targetDist, pd0 = nav.posDist;
        const r = orig.call(nav, a, b);
        const s = own >= 0 ? own : R.src;
        R.note(s, 0, t0, nav.targetIndex, td0, nav.targetDist);
        R.note(s, 1, p0, nav.positionIndex, pd0, nav.posDist);
        return r;
      };
    };
    wrapM('liveAppended', SRC.VIVO); wrapM('dataShifted', -1); wrapM('setTargetRel', -1); wrapM('setPosRel', -1); wrapM('reanchor', SRC.REANCORA);
    const wrapV = (view: any, which: number) => {
      const d = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(view), 'current');
      if (!d || !d.get || !d.set) return;
      const get = d.get, set = d.set;
      Object.defineProperty(view, 'current', {
        configurable: true, enumerable: true,
        get() { return get.call(view); },
        set(v: number) {
          const i0 = which ? nav.positionIndex : nav.targetIndex, d0 = which ? nav.posDist : nav.targetDist;
          set.call(view, v);
          R.note(R.src, which, i0, which ? nav.positionIndex : nav.targetIndex, d0, which ? nav.posDist : nav.targetDist);
        },
      });
    };
    wrapV(nav.target, 0); wrapV(nav.position, 1);
  }

  // ---------------------------------------------------------------- T2: dedo × posição (acumulado no quadro)
  private fPed = 0; private fApl = 0; private fLim = 0; private fMoves = 0;
  /** O arrasto: `ped` = Δ pedido pelo dedo (velas, antes da trava); `apl` = Δ aplicado ao alvo; `lim` = trava que atuou. */
  drag(ped: number, apl: number, lim: number): void { this.fPed += ped; this.fApl += apl; if (lim) this.fLim = lim; this.fMoves++; this.lastApl = apl; }
  /** P1c: Δ aplicado ao alvo pelo último movimento do dedo e distância do alvo ao fim agora (só leitura) */
  lastApl = 0;
  get targetNow(): number { return this.nav ? this.nav.targetDist : NaN; }

  // ---------------------------------------------------------------- quadros (anel de 240) e contra-arrasto
  private fHead = 0; fN = 0;
  frT = new Float64Array(F_CAP); frMode = new Uint8Array(F_CAP); frFinger = new Uint8Array(F_CAP);
  frPed = new Float32Array(F_CAP); frApl = new Float32Array(F_CAP); frMoved = new Float32Array(F_CAP);
  frTgt = new Float32Array(F_CAP); frLim = new Uint8Array(F_CAP); frSrc = new Uint16Array(F_CAP); frDt = new Float32Array(F_CAP);
  private lastPos = NaN; private prevT = 0; private holdRun = 0; private holdFired = false;
  /** último quadro (para a reclassificação dos pulos da P1) */
  lastPed = 0; lastMoved = 0; lastFinger = 0;
  /**
   * Um chamado por quadro (rAF do painel). `mode` = modo da navegação (1 = arrasto manual = dedo na tela).
   * Fecha o quadro: Δ da posição desde o quadro anterior, pedido/aplicado do dedo, fontes das escritas; detecta o
   * contra-arrasto.
   */
  frame(now: number, mode: number): void {
    const nav = this.nav;
    if (!nav) return;
    const pos = nav.positionIndex;
    const moved = this.lastPos === this.lastPos ? pos - this.lastPos : 0;
    this.lastPos = pos;
    const dt = this.prevT > 0 ? now - this.prevT : 0; this.prevT = now;
    const finger = mode === 1 ? 1 : 0;
    const i = this.fHead; this.fHead = (i + 1) % F_CAP; this.fN++;
    this.frT[i] = now; this.frMode[i] = mode; this.frFinger[i] = finger; this.frPed[i] = this.fPed; this.frApl[i] = this.fApl;
    this.frMoved[i] = moved; this.frTgt[i] = nav.targetDist; this.frLim[i] = this.fLim; this.frSrc[i] = this.frameSrcMask; this.frDt[i] = dt;
    this.lastPed = this.fPed; this.lastMoved = moved; this.lastFinger = finger;
    if (finger) this.detect(now, i, moved);
    else { this.holdRun = 0; this.holdFired = false; }
    this.fPed = 0; this.fApl = 0; this.fLim = 0; this.fMoves = 0; this.frameSrcMask = 0; this.framePosOther = 0; this.framePosOtherSrc = 0;
  }
  private detect(now: number, i: number, moved: number): void {
    const ped = this.fPed, ap = ped < 0 ? -ped : ped, am = moved < 0 ? -moved : moved;
    // (a) sentido oposto
    if (ap >= OPP_MIN && am >= 0.5 && moved * ped < 0) this.addEvent(now, 0, i, ped, moved);
    // (b) segura: anda menos de 30% do pedido por 3 quadros seguidos (uma vez por sequência)
    if (ap >= 1 && am < HOLD_RATIO * ap) {
      this.holdRun++;
      if (this.holdRun >= HOLD_FRAMES && !this.holdFired) { this.holdFired = true; this.addEvent(now, 1, i, ped, moved); }
    } else { this.holdRun = 0; this.holdFired = false; }
    // (c) a posição mudou por escrita de outra fonte com o dedo na tela
    if (this.framePosOther !== 0) this.addEvent(now, 2, i, ped, moved);
  }

  // ---------------------------------------------------------------- T3: eventos de contra-arrasto
  evN = 0; private evHead = 0;
  eT = new Float64Array(EV_CAP); eKind = new Uint8Array(EV_CAP); eDir = new Uint8Array(EV_CAP); eBand = new Uint8Array(EV_CAP);
  eLim = new Uint8Array(EV_CAP); eSrc30 = new Uint16Array(EV_CAP); eOther = new Uint16Array(EV_CAP); eLoad = new Uint8Array(EV_CAP);
  ePed = new Float32Array(EV_CAP); eMoved = new Float32Array(EV_CAP); eTgt = new Float32Array(EV_CAP);
  /** contagem [tipo][direção 0 = rumo ao presente, 1 = rumo ao passado][faixa] */
  readonly evCount = new Float64Array(3 * 2 * 3);
  readonly evSrcCount = new Float64Array(NSRC);
  evMaxMag = 0; private evMaxIdx = -1;
  private addEvent(now: number, kind: number, fi: number, ped: number, moved: number): void {
    const nav = this.nav!;
    const k = this.evHead; this.evHead = (k + 1) % EV_CAP; this.evN++;
    const dir = ped < 0 ? 0 : 1, tgt = nav.targetDist, band = tgt < 10 ? 0 : tgt < 1000 ? 1 : 2;
    let src30 = 0;
    for (let b = 0; b < LOOKBACK && b < this.fN; b++) src30 |= this.frSrc[(fi - b + F_CAP) % F_CAP];
    // carga, despejo ou costura em curso (eventos do gravador de quadros no último 1 s)
    const m = jank ? jank.maskBetween(now - 1000, now) : 0;
    const LOAD = (1 << 2) | (1 << 3) | (1 << 5), EVICT = (1 << 23), STITCH = (1 << 24);
    this.eT[k] = now; this.eKind[k] = kind; this.eDir[k] = dir; this.eBand[k] = band; this.eLim[k] = this.fLim;
    this.eSrc30[k] = src30; this.eOther[k] = this.framePosOtherSrc; this.eLoad[k] = (m & LOAD ? 1 : 0) | (m & EVICT ? 2 : 0) | (m & STITCH ? 4 : 0);
    this.ePed[k] = ped; this.eMoved[k] = moved; this.eTgt[k] = tgt;
    this.evCount[(kind * 2 + dir) * 3 + band]++;
    for (let s = 0; s < NSRC; s++) if (src30 & (1 << s)) this.evSrcCount[s]++;
    const mag = Math.abs(moved - ped);
    if (mag > this.evMaxMag) { this.evMaxMag = mag; this.evMaxIdx = k; }
    if (this.evSnaps < SNAP_EVENTS) { this.evSnaps++; this.snap(now, 1 + kind); }
  }

  // ---------------------------------------------------------------- T4: gravador de voo (instantâneos de 120 quadros)
  private evSnaps = 0; snapN = 0; private snapHead = 0;
  snT = new Float64Array(SNAP_CAP); snWhy = new Uint8Array(SNAP_CAP); snLen = new Uint16Array(SNAP_CAP);
  sT = new Float64Array(SNAP_CAP * SNAP_FRAMES); sMode = new Uint8Array(SNAP_CAP * SNAP_FRAMES); sFinger = new Uint8Array(SNAP_CAP * SNAP_FRAMES);
  sPed = new Float32Array(SNAP_CAP * SNAP_FRAMES); sMoved = new Float32Array(SNAP_CAP * SNAP_FRAMES); sTgt = new Float32Array(SNAP_CAP * SNAP_FRAMES);
  sLim = new Uint8Array(SNAP_CAP * SNAP_FRAMES); sSrc = new Uint16Array(SNAP_CAP * SNAP_FRAMES); sDt = new Float32Array(SNAP_CAP * SNAP_FRAMES);
  /** Congela os últimos 120 quadros. `why`: 0 = Marcar, 1/2/3 = contra-arrasto (sentido oposto / segura / outra fonte). */
  snap(now: number, why: number): void {
    const s = this.snapHead; this.snapHead = (s + 1) % SNAP_CAP; this.snapN++;
    const len = Math.min(SNAP_FRAMES, this.fN), o = s * SNAP_FRAMES;
    for (let k = 0; k < len; k++) {
      const i = (this.fHead - len + k + F_CAP * 2) % F_CAP, j = o + k;
      this.sT[j] = this.frT[i]; this.sMode[j] = this.frMode[i]; this.sFinger[j] = this.frFinger[i]; this.sPed[j] = this.frPed[i];
      this.sMoved[j] = this.frMoved[i]; this.sTgt[j] = this.frTgt[i]; this.sLim[j] = this.frLim[i]; this.sSrc[j] = this.frSrc[i]; this.sDt[j] = this.frDt[i];
    }
    this.snT[s] = now; this.snWhy[s] = why; this.snLen[s] = len;
  }

  // ---------------------------------------------------------------- relatórios (fora do caminho quente)
  private srcList(mask: number): string { const a: string[] = []; for (let s = 0; s < NSRC; s++) if (mask & (1 << s)) a.push(SRC_NAMES[s].split(' (')[0]); return a.join('+') || '—'; }
  private srcShort(mask: number): string { const a: string[] = []; for (let s = 0; s < NSRC; s++) if (mask & (1 << s)) a.push(String(s)); return a.join('+'); }
  /** CONTRA-ARRASTO (topo do resumo). */
  counterReport(t0Abs: number, lines: number): string[] {
    const L: string[] = [];
    const rel = (t: number) => f((t - t0Abs) / 1000, 1) + 's';
    const byDir = [0, 0], byBand = [0, 0, 0], byKind = [0, 0, 0];
    for (let kind = 0; kind < 3; kind++) for (let dir = 0; dir < 2; dir++) for (let b = 0; b < 3; b++) { const c = this.evCount[(kind * 2 + dir) * 3 + b]; byDir[dir] += c; byBand[b] += c; byKind[kind] += c; }
    L.push(`=== CONTRA-ARRASTO (dedo na tela): ${this.evN} | rumo ao presente ${byDir[0]} | rumo ao passado ${byDir[1]} | ${EV_KIND.map((k, i) => `${k} ${byKind[i]}`).join(' | ')} ===`);
    L.push(`distância ao presente: ${BAND_NAMES.map((b, i) => `${b} ${byBand[i]}`).join(' | ')}`);
    const dirBand: string[] = [];
    for (let dir = 0; dir < 2; dir++) for (let b = 0; b < 3; b++) { let c = 0; for (let kind = 0; kind < 3; kind++) c += this.evCount[(kind * 2 + dir) * 3 + b]; if (c) dirBand.push(`${dir ? 'passado' : 'presente'}/${BAND_NAMES[b]} ${c}`); }
    if (dirBand.length) L.push(`por direção e faixa: ${dirBand.join(' | ')}`);
    const top = [...this.evSrcCount].map((c, s) => [c, s]).filter(x => x[0] > 0).sort((a, b) => b[0] - a[0]).slice(0, 6);
    if (top.length) L.push(`fontes das escritas nos 30 quadros anteriores (eventos): ${top.map(([c, s]) => `${SRC_NAMES[s].split(' (')[0]} ${c}`).join(' | ')}`);
    if (this.evMaxIdx >= 0) { const k = this.evMaxIdx; L.push(`maior: ${this.eventLine(k, rel)}`); }
    const sz = Math.min(this.evN, EV_CAP);
    for (let q = Math.max(0, sz - lines); q < sz; q++) { const k = (this.evHead - sz + q + EV_CAP * 2) % EV_CAP; L.push(this.eventLine(k, rel)); }
    return L;
  }
  private eventLine(k: number, rel: (t: number) => string): string {
    const ld = this.eLoad[k], load = [ld & 1 ? 'carga' : '', ld & 2 ? 'despejo' : '', ld & 4 ? 'costura' : ''].filter(Boolean).join('+') || 'nada';
    return `t=${rel(this.eT[k])} ${EV_KIND[this.eKind[k]]} | dedo rumo ao ${this.eDir[k] ? 'passado' : 'presente'} ${f(this.ePed[k], 1)} → vista ${f(this.eMoved[k], 1)} velas | a ${f(this.eTgt[k], 0)} velas do fim (${BAND_NAMES[this.eBand[k]]}) | trava: ${LIM_NAMES[this.eLim[k]]}${this.eOther[k] ? ` | escrita de ${this.srcList(this.eOther[k])}` : ''} | 30 quadros: ${this.eSrc30[k] ? this.srcList(this.eSrc30[k]) : 'nenhuma escrita mudou a vista'} | carga/despejo/costura em 1 s: ${load}`;
  }
  /** Registro de escritas: contagem por fonte. */
  writesReport(): string[] {
    const parts: string[] = [];
    for (let s = 0; s < NSRC; s++) if (this.srcCount[s] > 0) parts.push(`${SRC_NAMES[s].split(' (')[0]} ${this.srcCount[s]}`);
    return [`escritas na navegação: ${this.wN} | ${parts.join(' | ') || '—'}`];
  }
  /** Instantâneos do gravador de voo. `full`: todos, 1 linha por quadro com dedo ou escrita; senão os 2 últimos em 10 quadros. */
  snapsReport(t0Abs: number, full: boolean): string[] {
    const L: string[] = [];
    const sz = Math.min(this.snapN, SNAP_CAP), why = ['Marcar', ...EV_KIND];
    L.push(`=== GRAVADOR DE VOO: ${this.snapN} instantâneos de ${SNAP_FRAMES} quadros (Marcar e contra-arrasto) ===`);
    L.push(`legenda: Δt(ms antes do instantâneo) modo dedo(pedido) vista(Δ aplicado) alvo(velas do fim) trava fontes quadro(ms) | fontes: ${SRC_NAMES.map((n, i) => `${i}=${n.split(' (')[0]}`).join(' ')}`);
    const first = full ? 0 : Math.max(0, sz - 2);
    for (let q = first; q < sz; q++) {
      const s = (this.snapHead - sz + q + SNAP_CAP * 2) % SNAP_CAP, o = s * SNAP_FRAMES, len = this.snLen[s];
      L.push(`--- instantâneo t=${f((this.snT[s] - t0Abs) / 1000, 1)}s (${why[this.snWhy[s]]}) ---`);
      const rows: number[] = [];
      for (let k = 0; k < len; k++) { const j = o + k; if (this.sFinger[j] || this.sSrc[j] || this.sMoved[j] !== 0) rows.push(j); }
      const pick = full ? rows : rows.slice(-10);
      for (const j of pick) L.push(`${f(this.sT[j] - this.snT[s], 0)} ${MODE_NAMES[this.sMode[j]].split(' ')[0]} ${this.sFinger[j] ? f(this.sPed[j], 1) : '·'} ${f(this.sMoved[j], 1)} ${f(this.sTgt[j], 0)} ${LIM_SHORT[this.sLim[j]]} ${this.srcShort(this.sSrc[j]) || '·'} ${f(this.sDt[j], 0)}`);
    }
    return L;
  }
  /** Últimas escritas (log completo). */
  writesList(t0Abs: number, lines: number): string[] {
    const L: string[] = [`=== ESCRITAS NA NAVEGAÇÃO (últimas ${Math.min(lines, this.wN)} de ${this.wN}; vela = índice ancorado, dist = velas do fim) ===`];
    const sz = Math.min(this.wN, W_CAP);
    for (let q = Math.max(0, sz - lines); q < sz; q++) {
      const i = (this.wHead - sz + q + W_CAP * 2) % W_CAP;
      L.push(`q${this.wFrame[i]} t=${f((this.wT[i] - t0Abs) / 1000, 2)}s ${SRC_NAMES[this.wSrc[i]].split(' (')[0]} ${WHICH[this.wWhich[i]]}: vela ${f(this.wBefore[i], 1)} → ${f(this.wAfter[i], 1)} (Δ ${f(this.wAfter[i] - this.wBefore[i], 1)}) | dist ${f(this.wDistBefore[i], 1)} → ${f(this.wDistAfter[i], 1)}`);
    }
    return L;
  }
}

export const navRec: NavRecorder | null = DBG_ENABLED ? new NavRecorder() : null;

/** Marca a fonte das próximas escritas na navegação (0 = volta a "outra"). Sem a flag não faz nada. */
export function navSource(src: number): void { if (DBG_ENABLED) navRec!.src = src; }
/** O arrasto informa o Δ pedido pelo dedo, o Δ aplicado e a trava. Sem a flag não faz nada. */
export function navDrag(ped: number, apl: number, lim: number): void { if (DBG_ENABLED) navRec!.drag(ped, apl, lim); }
